const API = process.env.REACT_APP_BACKEND_URL;

let preloadStarted = false;

// Preload une image dans le cache navigateur
const preloadImage = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

// Preload une vidéo dans le cache navigateur
const preloadVideo = (url) => {
  return fetch(url, { mode: 'cors' })
    .then(() => true)
    .catch(() => false);
};

const resolveUrl = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${API}${url}`;
};

// Pré-charge le fond par défaut en priorité haute (appelé dès le mount de App)
export const preloadDefaults = async () => {
  try {
    const [breathImgRes, breathVidRes, scanImgRes, scanVidRes] = await Promise.all([
      fetch(`${API}/api/breathing-config/default-image`).then(r => r.json()).catch(() => ({})),
      fetch(`${API}/api/breathing-config/default-video`).then(r => r.json()).catch(() => ({})),
      fetch(`${API}/api/bodyscan-config/default-image`).then(r => r.json()).catch(() => ({})),
      fetch(`${API}/api/bodyscan-config/default-video`).then(r => r.json()).catch(() => ({})),
    ]);

    const promises = [];

    // Pré-charger les images et vidéos par défaut
    if (breathImgRes.image_id) {
      const images = await fetch(`${API}/api/breathing-images`).then(r => r.json()).catch(() => []);
      const defaultImg = images.find(i => i.id === breathImgRes.image_id);
      if (defaultImg) promises.push(preloadImage(resolveUrl(defaultImg.url)));
    }
    if (breathVidRes.video_id) {
      const videos = await fetch(`${API}/api/breathing-videos`).then(r => r.json()).catch(() => []);
      const defaultVid = videos.find(v => v.id === breathVidRes.video_id);
      if (defaultVid) promises.push(preloadVideo(resolveUrl(defaultVid.url)));
    }
    if (scanImgRes.image_id) {
      const images = await fetch(`${API}/api/breathing-images`).then(r => r.json()).catch(() => []);
      const defaultImg = images.find(i => i.id === scanImgRes.image_id);
      if (defaultImg) promises.push(preloadImage(resolveUrl(defaultImg.url)));
    }
    if (scanVidRes.video_id) {
      const videos = await fetch(`${API}/api/bodyscan-videos`).then(r => r.json()).catch(() => []);
      const defaultVid = videos.find(v => v.id === scanVidRes.video_id);
      if (defaultVid) promises.push(preloadVideo(resolveUrl(defaultVid.url)));
    }

    await Promise.all(promises);
  } catch (e) {
    console.warn('Default preload failed (offline?):', e.message);
  }
};

// Pré-charge TOUS les médias en arrière-plan (pour le hors-ligne)
export const preloadAllMedia = async (onProgress) => {
  if (preloadStarted) return;
  preloadStarted = true;

  try {
    const [breathVideos, breathImages, scanVideos] = await Promise.all([
      fetch(`${API}/api/breathing-videos`).then(r => r.json()).catch(() => []),
      fetch(`${API}/api/breathing-images`).then(r => r.json()).catch(() => []),
      fetch(`${API}/api/bodyscan-videos`).then(r => r.json()).catch(() => []),
    ]);

    const allUrls = [
      ...breathImages.map(i => ({ url: resolveUrl(i.url), type: 'image' })),
      ...breathVideos.map(v => ({ url: resolveUrl(v.url), type: 'video' })),
      ...scanVideos.map(v => ({ url: resolveUrl(v.url), type: 'video' })),
    ].filter(u => u.url);

    let loaded = 0;
    const total = allUrls.length;

    // Charger par lots de 3 pour ne pas surcharger le réseau
    for (let i = 0; i < allUrls.length; i += 3) {
      const batch = allUrls.slice(i, i + 3);
      await Promise.all(batch.map(item =>
        item.type === 'image'
          ? preloadImage(item.url)
          : preloadVideo(item.url)
      ));
      loaded += batch.length;
      if (onProgress) onProgress(loaded, total);
    }

    // Demander au Service Worker de mettre en cache aussi
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'PRECACHE_MEDIA',
        urls: allUrls.map(u => u.url)
      });
    }
  } catch (e) {
    console.warn('Full preload failed (offline?):', e.message);
  }
};
